import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Barra } from '@/componentes/Esqueleto';
import { formatarData, formatarDecimal, formatarMesPorExtenso, formatarReais, formatarReaisCompacto } from '@/lib/formato';
import { perfilAPedir } from '@/lib/perfil-da-licenca';
import { useDepreciacaoDaMaquina } from '@/modulos/custos/consultas';
import { JanelaDaDepreciacao } from '@/modulos/custos/DepreciacaoDasMaquinas';
import { parcelaEscrita, rotuloDaMargem } from '@/modulos/custos/margem';
import { numeroDaOrdem } from '@/modulos/oficina/ordem';
import { useSessao } from '@/modulos/sessao/consultas';

import type { CustoDoMes, OrdensDoProntuario, Prontuario, Secao } from './frota';

/**
 * OS DOIS BLOCOS NOVOS DA FICHA (R06, p.71), abaixo dos dados, nesta ordem:
 * "Custo da máquina" (o mês fechado: custo direto, por hora e margem, com a
 * divisão Combustível · Peças · Terceiros · Outros) e "Peças aplicadas" (das
 * O.S. encerradas e em execução, a mais recente primeiro).
 *
 * A margem é a receita das medições aprovadas da máquina no mês contra o
 * CUSTO TOTAL (0096): o direto — com a mão de obra pelo valor-hora da função —,
 * a depreciação linear e o custo indireto rateado
 * (`backend/src/rotas/equipamentos/custo-do-mes.ts`). Ela se diz "Margem" só
 * quando nada ficou fora do mês; senão é "Margem parcial", com as ausências
 * que o servidor escreve. Sem base, o valor é "—" com a frase do porquê —
 * nunca zero. A depreciação da máquina se cadastra aqui mesmo, por quem tem
 * `depreciacao.gravar` (dúvida de design: o desenho R06 não tem a linha).
 */
export function BlocosDeCustoEPecas({ prontuario, carregando }: { readonly prontuario: Prontuario | undefined; readonly carregando: boolean }) {
  /* O prontuário recusado ou que não veio: o bloco do uso, acima, já diz o
     porquê — o esqueleto aqui ficaria para sempre dizendo "está vindo". */
  if (!carregando && !prontuario) return null;
  if (carregando || !prontuario) {
    return (
      <div aria-hidden="true" className="grade-do-painel">
        {['Custo da máquina', 'Peças aplicadas'].map((t, i) => (
          <section key={t} className={`cartao flex min-w-0 flex-col gap-3 p-4 ${i === 0 ? 'campo-largo' : ''}`}>
            <h3 className="txt-subtitulo text-tinta">{t}</h3>
            <Barra className="h-6 w-3/4" />
            <Barra className="h-3 w-full" />
          </section>
        ))}
      </div>
    );
  }
  /* Na janela larga do painel (pedido do dono, 31/10/2026), lado a lado: o
     custo em duas das tres colunas, as pecas na terceira. */
  return (
    <div className="grade-do-painel">
      <CustoDaMaquina secao={prontuario.custoDoMes} equipamentoId={prontuario.equipamento.id} />
      <PecasAplicadas secao={prontuario.ordens} />
    </div>
  );
}

const PARCELAS = [
  { chave: 'combustivel', rotulo: 'Combustível', cor: 'bg-grafico-1' },
  { chave: 'estoque', rotulo: 'Peças', cor: 'bg-grafico-2' },
  { chave: 'terceiros', rotulo: 'Terceiros', cor: 'bg-grafico-3' },
  { chave: 'outros', rotulo: 'Outros', cor: 'bg-grafico-outros' },
] as const;

function CustoDaMaquina({ secao, equipamentoId }: { readonly secao: Secao<CustoDoMes>; readonly equipamentoId: string }) {
  const sessao = useSessao();
  const podeGravar = (sessao.data?.permissoes ?? []).includes('depreciacao.gravar');
  const { maquina } = useDepreciacaoDaMaquina(equipamentoId, secao.permitido);
  const [definindo, setDefinindo] = useState(false);
  if (!secao.permitido) {
    return (
      <section className="campo-largo cartao flex min-w-0 flex-col gap-2 p-4" aria-label="Custo da máquina">
        <h3 className="txt-subtitulo text-tinta">Custo da máquina</h3>
        <p className="txt-apoio text-tinta-secundaria">{`Você não tem acesso a Custos. Peça ao administrador da sua empresa o perfil ${perfilAPedir('custo.ler')}.`}</p>
      </section>
    );
  }
  const v = secao.valores;
  const total = v?.total ?? 0;
  return (
    <section className="campo-largo cartao flex min-w-0 flex-col gap-4 p-4" aria-label="Custo da máquina">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="txt-subtitulo text-tinta">Custo da máquina</h3>
        <span className="txt-apoio text-tinta-secundaria">{formatarMesPorExtenso(secao.competencia)}</span>
      </div>
      {v === null ? (
        <p className="txt-apoio text-tinta-secundaria">
          A máquina está inativa, e a conta de custo só soma a frota ativa. Reative a máquina para ela voltar à conta.
        </p>
      ) : (
        <>
          <dl className="flex flex-wrap justify-between gap-x-4 gap-y-3">
            <div className="flex flex-col gap-1">
              <dt className="txt-legenda font-normal text-tinta-secundaria">{secao.completo ? 'Custo total' : 'Custo total, parcial'}</dt>
              <dd className="txt-secao whitespace-nowrap tabular-nums text-tinta" title={formatarReais(v.custoTotal)}>
                {formatarReaisCompacto(v.custoTotal)}
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="txt-legenda font-normal text-tinta-secundaria">Por hora</dt>
              <dd className="txt-secao whitespace-nowrap tabular-nums text-tinta">{v.custoTotalPorHora === null ? '—' : formatarReais(v.custoTotalPorHora)}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="txt-legenda font-normal text-tinta-secundaria">{rotuloDaMargem(secao.completo)}</dt>
              {/* Valor em preto; vermelho só quando o próprio valor é o alerta: margem negativa. */}
              <dd className={`txt-secao whitespace-nowrap tabular-nums ${secao.margem !== null && secao.margem < 0 ? 'text-parada-texto' : 'text-tinta'}`}>
                {secao.margem === null ? '—' : `${formatarDecimal(secao.margem * 100, 1)}%`}
              </dd>
            </div>
          </dl>

          {/* A divisão do custo DIRETO (p.71): a barra de 6 px com as quatro parcelas, e a legenda escrita. */}
          {total > 0 ? (
            <div
              role="img"
              aria-label={PARCELAS.map((p) => `${p.rotulo} ${formatarReais(v[p.chave])}`).join(', ')}
              className="flex h-[var(--indicador-barra)] w-full gap-1 overflow-hidden rounded-pill"
            >
              {PARCELAS.map((p) =>
                v[p.chave] > 0 ? <span key={p.chave} className={`h-full rounded-pill ${p.cor}`} style={{ flexGrow: v[p.chave] }} /> : null,
              )}
            </div>
          ) : null}
          <ul className="flex flex-wrap gap-x-4 gap-y-1">
            {PARCELAS.map((p) => (
              <li key={p.chave} className="txt-apoio flex items-center gap-1 text-tinta-secundaria" title={formatarReais(v[p.chave])}>
                <span aria-hidden="true" className={`size-2 rounded-marcador ${p.cor}`} />
                {p.rotulo}
              </li>
            ))}
          </ul>

          {/* 0096 · do direto ao total: as duas parcelas que não são do fato da máquina. */}
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="txt-apoio text-tinta-secundaria">Direto</dt>
            <dd className="txt-apoio text-right tabular-nums text-tinta">{formatarReais(total)}</dd>
            <dt className="txt-apoio text-tinta-secundaria">Depreciação</dt>
            <dd className="txt-apoio text-right tabular-nums text-tinta">
              {v.semDepreciacao ? 'sem dado, fora do total' : formatarReais(v.depreciacao)}
            </dd>
            <dt className="txt-apoio text-tinta-secundaria">Custo indireto rateado</dt>
            <dd className="txt-apoio text-right tabular-nums text-tinta">{formatarReais(v.indiretoRateado)}</dd>
          </dl>

          <p className="txt-apoio text-tinta-apagada">
            {[
              `De ${formatarData(new Date(`${secao.de}T12:00:00`))} a ${formatarData(new Date(`${secao.ate}T12:00:00`))}`,
              v.custoTotalPorHora === null ? 'sem hora produtiva apontada' : `por ${secao.denominador}`,
              secao.margem === null
                ? (secao.semMargemPorque ?? 'sem base para a margem')
                : secao.completo
                  ? 'margem: receita das medições aprovadas contra o custo total'
                  : 'margem parcial: receita das medições aprovadas contra o custo total, sem o que ficou fora',
              v.maoDeObra > 0 ? `Outros: mão de obra pelo valor-hora da função, ${formatarReais(v.maoDeObra)}` : null,
              /* As ausências do mês, como o servidor escreveu (sem preço, hora
                 sem valor, sem depreciação, indireto sem lançamento). */
              ...secao.ausencias,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/custos" className="botao botao-fantasma botao-p">
              Abrir custos
            </Link>
            {podeGravar ? (
              <Botao
                nivel="fantasma"
                tamanho="p"
                disabled={maquina === null}
                title={maquina?.vigente ? `Hoje: ${parcelaEscrita(maquina.vigente.parcelaMensal)}` : undefined}
                onClick={() => {
                  setDefinindo(true);
                }}
              >
                {maquina?.vigente ? 'Corrigir depreciação' : 'Definir depreciação'}
              </Botao>
            ) : null}
          </div>
          <JanelaDaDepreciacao
            maquina={definindo && podeGravar ? maquina : null}
            aoFechar={() => {
              setDefinindo(false);
            }}
          />
        </>
      )}
    </section>
  );
}

function PecasAplicadas({ secao }: { readonly secao: Secao<OrdensDoProntuario> }) {
  return (
    <section className="cartao flex min-w-0 flex-col gap-2 p-4" aria-label="Peças aplicadas">
      <h3 className="txt-subtitulo text-tinta">Peças aplicadas</h3>
      {!secao.permitido ? (
        <p className="txt-apoio text-tinta-secundaria">{`Você não tem acesso à oficina. Peça ao administrador da sua empresa o perfil ${perfilAPedir('os.ler')}.`}</p>
      ) : secao.pecasAplicadas.length === 0 ? (
        <p className="txt-apoio text-tinta-secundaria">
          Nenhuma peça aplicada nas O.S. desta máquina. A peça entra aqui quando a oficina a marca como aplicada.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-borda-sutil">
          {secao.pecasAplicadas.map((p) => (
            <li key={p.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2">
              <span className="txt-corpo font-semibold text-tinta">{p.descricao}</span>
              <span className="txt-apoio tabular-nums text-tinta-secundaria">
                {[
                  `${formatarDecimal(p.quantidade, 1).replace(/,0$/, '')} ${p.unidade}`,
                  numeroDaOrdem(p.numero),
                  p.aplicadaEm ? formatarData(p.aplicadaEm).slice(0, 5) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
